import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Lightbulb, Calendar, Edit, Trash2, AlertTriangle, CheckCircle, XCircle, History, Clock } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { usePostesIluminacao } from '../context/PostesIluminacaoProvider';
import { formatarData } from '../utils/formatters';
import { formatarCoordenadas, createPosteIcon } from '../utils/mapHelpers';
export default function DetalhesPoste() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { buscarPostePorId, excluirPoste, carregarHistoricoStatus } = usePostesIluminacao();

  const [poste, setPoste] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [mostrarModalExclusao, setMostrarModalExclusao] = useState(false);

  useEffect(() => {
    carregarPoste();
  }, [id]);

  const carregarPoste = async () => {
    try {
      setLoading(true);
      const dadosPoste = await buscarPostePorId(id);
      setPoste(dadosPoste);

      // Carregar histórico após carregar o poste
      await carregarHistorico();
    } catch (error) {
      console.error('Erro ao carregar poste:', error);
      navigate('/postes-iluminacao');
    } finally {
      setLoading(false);
    }
  };

  const carregarHistorico = async () => {
    try {
      setLoadingHistorico(true);
      const dadosHistorico = await carregarHistoricoStatus(id);
      setHistorico(dadosHistorico.slice(0, 10)); // Limitar aos últimos 10 registros
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  };

  const handleExcluir = async () => {
    try {
      await excluirPoste(id);
      navigate('/postes-iluminacao');
    } catch (error) {
      console.error('Erro ao excluir poste:', error);
    }
  };

  const obterIconeStatus = (status) => {
    switch (status) {
      case 'Funcionando':
        return <CheckCircle size={20} color="#4caf50" />;
      case 'Alerta':
        return <AlertTriangle size={20} color="#ff9800" />;
      case 'Defeito':
        return <XCircle size={20} color="#f44336" />;
      default:
        return <Lightbulb size={20} color="#666" />;
    }
  };

  const obterCorStatus = (status) => {
    switch (status) {
      case 'Funcionando':
        return '#4caf50';
      case 'Alerta':
        return '#ff9800';
      case 'Defeito':
        return '#f44336';
      default:
        return '#666';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando detalhes do poste...</p>
      </div>
    );
  }

  if (!poste) {
    return (
      <div className="error-container">
        <h2>Poste não encontrado</h2>
        <p>O poste solicitado não foi encontrado.</p>
        <Link to="/postes-iluminacao" className="button primary">
          <ArrowLeft size={16} />
          Voltar para a lista
        </Link>
      </div>
    );
  }

  return (
    <div className="postes-iluminacao">
      <div className="detalhes-poste-container">
        <div className="detalhes-header">
          <button
            onClick={() => navigate('/postes-iluminacao')}
            className="back-button"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="header-content">
            <h1>
              <Lightbulb size={24} color={obterCorStatus(poste.status)} />
              Detalhes do Poste #{poste.numero_identificacao}
            </h1>
            <p>Informações completas do poste de iluminação pública</p>
          </div>
          <div className="header-actions">
            <Link
              to={`/postes-iluminacao/editar/${poste.id}`}
              className="button secondary"
            >
              <Edit size={16} />
              Editar
            </Link>
            <button
              onClick={() => setMostrarModalExclusao(true)}
              className="button danger"
            >
              <Trash2 size={16} />
              Excluir
            </button>
          </div>
        </div>

        <div className="detalhes-content"></div>

        {/* Layout Principal em Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 350px',
          gap: '30px',
          marginBottom: '30px'
        }}>
          {/* Coluna Esquerda - Informações */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>

            {/* Informações Básicas */}
            <div className="form-section">
              <h3>Informações Básicas</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div className="info-item">
                  <label>Número de Identificação</label>
                  <div className="info-value">#{poste.numero_identificacao}</div>
                </div>

                <div className="info-item">
                  <label>Status</label>
                  <div className="info-value" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: obterCorStatus(poste.status)
                  }}>
                    {obterIconeStatus(poste.status)}
                    {poste.status}
                  </div>
                </div>

                <div className="info-item">
                  <label>Bairro</label>
                  <div className="info-value">
                    {poste.ccbairros?.descricao || 'Não informado'}
                  </div>
                </div>
              </div>
            </div>

            {/* Especificações Técnicas */}
            <div className="form-section">
              <h3>Especificações Técnicas</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div className="info-item">
                  <label>Tipo de Lâmpada</label>
                  <div className="info-value">{poste.tipo_lampada || 'Não informado'}</div>
                </div>

                <div className="info-item">
                  <label>Potência</label>
                  <div className="info-value">{poste.potencia || 'Não informada'}</div>
                </div>

                <div className="info-item">
                  <label>Altura do Poste</label>
                  <div className="info-value">{poste.altura_poste || 'Não informada'}</div>
                </div>

                <div className="info-item">
                  <label>Data de Instalação</label>
                  <div className="info-value">
                    <Calendar size={16} style={{ marginRight: '6px' }} />
                    {poste.data_instalacao ? formatarData(poste.data_instalacao) : 'Não informada'}
                  </div>
                </div>
              </div>
            </div>

            {/* Localização */}
            <div className="form-section">
              <h3>Localização</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                <div className="info-item">
                  <label>Coordenadas</label>
                  <div className="info-value" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontFamily: 'monospace',
                    fontSize: '14px'
                  }}>
                    <MapPin size={16} style={{ color: '#ff6f00' }} />
                    {formatarCoordenadas(poste.latitude, poste.longitude)}
                  </div>
                </div>

                <div className="info-item">
                  <label>Latitude</label>
                  <div className="info-value" style={{ fontFamily: 'monospace' }}>
                    {poste.latitude?.toFixed(6) || 'Não informada'}
                  </div>
                </div>

                <div className="info-item">
                  <label>Longitude</label>
                  <div className="info-value" style={{ fontFamily: 'monospace' }}>
                    {poste.longitude?.toFixed(6) || 'Não informada'}
                  </div>
                </div>
              </div>
            </div>

            {/* Observações */}
            {poste.observacoes && (
              <div className="form-section">
                <h3>Observações</h3>
                <div className="info-item">
                  <div className="info-value" style={{
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    lineHeight: '1.5'
                  }}>
                    {poste.observacoes}
                  </div>
                </div>
              </div>
            )}

            {/* Informações do Sistema */}
            <div className="form-section">
              <h3>Informações do Sistema</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div className="info-item">
                  <label>Data de Cadastro</label>
                  <div className="info-value">
                    <Calendar size={16} style={{ marginRight: '6px' }} />
                    {poste.created_at ? formatarData(poste.created_at) : 'Não disponível'}
                  </div>
                </div>

                <div className="info-item">
                  <label>Última Atualização</label>
                  <div className="info-value">
                    <Calendar size={16} style={{ marginRight: '6px' }} />
                    {poste.updated_at ? formatarData(poste.updated_at) : 'Não disponível'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Coluna Direita - Mapa e Histórico */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>

            {/* Mapa de Localização */}
            <div className="form-section">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <MapPin size={20} />
                Localização
              </h3>

              <div style={{
                height: '250px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #dee2e6',
                position: 'relative'
              }}>
                {poste.latitude && poste.longitude ? (
                  <MapContainer
                    center={[parseFloat(poste.latitude), parseFloat(poste.longitude)]}
                    zoom={16}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={true}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Marker
                      position={[parseFloat(poste.latitude), parseFloat(poste.longitude)]}
                      icon={createPosteIcon(poste.status)}
                    />
                  </MapContainer>
                ) : (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    color: '#666'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <MapPin size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                      <p>Coordenadas não disponíveis</p>
                    </div>
                  </div>
                )}
              </div>

              <div style={{
                marginTop: '10px',
                fontSize: '14px',
                color: '#666',
                fontFamily: 'monospace',
                textAlign: 'center'
              }}>
                {formatarCoordenadas(poste.latitude, poste.longitude) || 'Coordenadas não informadas'}
              </div>
            </div>

            {/* Histórico de Status */}
            <div className="form-section">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <History size={20} />
                Histórico de Status
              </h3>

              {loadingHistorico ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  color: '#666'
                }}>
                  <div className="loading-spinner" style={{ width: '20px', height: '20px', marginRight: '10px' }}></div>
                  Carregando histórico...
                </div>
              ) : historico.length > 0 ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {historico.map((item, index) => (
                    <div key={item.id || index} style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      fontSize: '14px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '6px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {item.status_anterior && (
                            <span style={{
                              padding: '2px 6px',
                              backgroundColor: obterCorStatus(item.status_anterior),
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {item.status_anterior}
                            </span>
                          )}
                          <span style={{ color: '#666' }}>→</span>
                          <span style={{
                            padding: '2px 6px',
                            backgroundColor: obterCorStatus(item.status_novo),
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {item.status_novo}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: '#666',
                          fontSize: '12px'
                        }}>
                          <Clock size={12} />
                          {formatarData(item.data_mudanca)}
                        </div>
                      </div>

                      {item.observacoes && (
                        <div style={{
                          marginTop: '6px',
                          padding: '6px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#555'
                        }}>
                          {item.observacoes}
                        </div>
                      )}

                      {item.responsavel && (
                        <div style={{
                          marginTop: '4px',
                          fontSize: '12px',
                          color: '#888'
                        }}>
                          Por: {item.responsavel}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#666',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <History size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p>Nenhum histórico de mudanças encontrado</p>
                  <p style={{ fontSize: '14px', marginTop: '4px' }}>
                    As alterações de status aparecerão aqui
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {mostrarModalExclusao && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
            </div>
            <div className="modal-content">
              <p>
                Tem certeza que deseja excluir o poste <strong>#{poste.numero_identificacao}</strong>?
              </p>
              <p style={{ color: '#dc3545', fontSize: '14px', marginTop: '10px' }}>
                Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setMostrarModalExclusao(false)}
                className="button secondary"
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluir}
                className="button danger"
              >
                <Trash2 size={16} />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
